// ═══════════════════════════════════════════════════════════════════════════════
// Invitation-code referrer stats — bound referrer only
// ═══════════════════════════════════════════════════════════════════════════════

import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  INVITATION_CODE_TYPES,
  type InvitationCodeReferrerStats,
} from '@fdh/shared';
import { useLanguage } from '../../contexts/LanguageContext';
import { settingsApi } from '../../lib/api';
import { navigateBack } from '../../lib/navigateBack';
import { SCROLL_BOTTOM_FOR_FIXED_ACTION_ROW } from '../../lib/layoutChrome';
import { AppButton } from '../../components/ui/AppButton';

export const InvitationCodeReferrerPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [stats, setStats] = useState<InvitationCodeReferrerStats | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const handleBack = () => {
    navigateBack(navigate, '/settings');
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await settingsApi.getInvitationCodeReferrerStats();
        if (response.success && response.data != null) {
          setStats(response.data);
        } else {
          setError(t('inviteReferrer.notBound'));
        }
      } catch {
        setError(t('inviteReferrer.notBound'));
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [t]);

  const handleCopyLink = useCallback(async () => {
    if (!stats?.inviteLink) {
      return;
    }
    try {
      await navigator.clipboard.writeText(stats.inviteLink);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [stats?.inviteLink]);

  const benefitLabel = (() => {
    if (!stats) {
      return null;
    }
    if (
      stats.codeType === INVITATION_CODE_TYPES.L1_TRIAL_EXTEND &&
      stats.levelDurationDays != null
    ) {
      return t('inviteReferrer.benefitL1Extend', { days: stats.levelDurationDays });
    }
    if (
      stats.codeType === INVITATION_CODE_TYPES.L3_TRIAL_PERIOD &&
      stats.levelDurationDays != null
    ) {
      return t('inviteReferrer.benefitL3Trial', { days: stats.levelDurationDays });
    }
    return null;
  })();

  const maxUsesLabel = stats
    ? stats.maxUses == null
      ? t('inviteReferrer.maxUsesUnlimited', { used: stats.totalUses })
      : t('inviteReferrer.maxUsesLimited', {
          used: stats.totalUses,
          max: stats.maxUses,
        })
    : '';

  const validityLabel = stats
    ? t('inviteReferrer.validityRange', {
        start: stats.startDate ?? t('inviteReferrer.validityImmediate'),
        end: stats.endDate ?? t('inviteReferrer.validityPermanent'),
      })
    : '';

  return (
    <div className={`min-h-full bg-gray-50 dark:bg-gray-950 ${SCROLL_BOTTOM_FOR_FIXED_ACTION_ROW}`}>
      <header className="z-10 bg-white shadow-sm dark:bg-gray-900 dark:shadow-black/20">
        <div className="mx-auto max-w-lg px-4 py-4">
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={handleBack}
              aria-label={t('common.back')}
              className="inline-flex min-h-11 min-w-11 items-center justify-center rounded-lg text-gray-700 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-200 dark:hover:bg-gray-800"
            >
              <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">
              {t('inviteReferrer.pageTitle')}
            </h1>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-lg px-4 py-6">
        {loading ? (
          <p className="text-sm text-gray-500" role="status">
            {t('common.loading')}
          </p>
        ) : null}

        {error ? (
          <p className="text-sm text-red-600 dark:text-red-400" role="alert">
            {error}
          </p>
        ) : null}

        {stats && !loading ? (
          <div className="space-y-4">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {t('inviteReferrer.pageSubtitle')}
            </p>

            <div
              className="rounded-xl border border-gray-200 bg-white px-4 py-3 dark:border-gray-700 dark:bg-gray-900"
              role="group"
              aria-label={t('inviteReferrer.statsLabel')}
            >
              <dl className="space-y-3 text-sm">
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-gray-500 dark:text-gray-400">{t('inviteReferrer.code')}</dt>
                  <dd className="font-mono font-semibold tracking-widest text-gray-900 dark:text-white">
                    {stats.code}
                  </dd>
                </div>
                {benefitLabel ? (
                  <div className="flex items-start justify-between gap-3">
                    <dt className="text-gray-500 dark:text-gray-400">
                      {t('inviteReferrer.benefit')}
                    </dt>
                    <dd className="text-right font-semibold text-gray-900 dark:text-white">
                      {benefitLabel}
                    </dd>
                  </div>
                ) : null}
                <div className="flex items-start justify-between gap-3">
                  <dt className="text-gray-500 dark:text-gray-400">
                    {t('inviteReferrer.maxUses')}
                  </dt>
                  <dd className="text-right font-semibold text-gray-900 dark:text-white">
                    {maxUsesLabel}
                  </dd>
                </div>
                <div className="flex items-start justify-between gap-3">
                  <dt className="text-gray-500 dark:text-gray-400">
                    {t('inviteReferrer.validity')}
                  </dt>
                  <dd className="text-right font-semibold text-gray-900 dark:text-white">
                    {validityLabel}
                  </dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-gray-500 dark:text-gray-400">{t('inviteReferrer.totalUses')}</dt>
                  <dd className="font-semibold text-gray-900 dark:text-white">{stats.totalUses}</dd>
                </div>
                <div className="flex items-center justify-between gap-3">
                  <dt className="text-gray-500 dark:text-gray-400">{t('inviteReferrer.paidUsers')}</dt>
                  <dd className="font-semibold text-gray-900 dark:text-white">
                    {stats.paidUserCount}
                  </dd>
                </div>
              </dl>
            </div>

            <div>
              <p className="text-xs font-medium text-gray-500 dark:text-gray-400">
                {t('inviteReferrer.shareLink')}
              </p>
              <p className="mt-1 break-all rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-xs text-gray-800 dark:border-gray-700 dark:bg-gray-950 dark:text-gray-100">
                {stats.inviteLink}
              </p>
              <AppButton
                type="button"
                variant="primary"
                className="mt-2"
                onClick={() => void handleCopyLink()}
                aria-label={t('inviteReferrer.copyLink')}
              >
                {copied ? t('inviteReferrer.copied') : t('inviteReferrer.copyLink')}
              </AppButton>
            </div>
          </div>
        ) : null}
      </main>
    </div>
  );
};
